import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import OfflineBanner from "components/OfflineBanner";
import HelpSidePanel from "components/HelpSidePanel";
import NotFound from "pages/NotFound";
import ProtectedRoute from "components/ProtectedRoute";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import EmailConfirmation from "./pages/email-confirmation";
import MainDashboard from './pages/main-dashboard';
import ActiveBatchManagement from './pages/active-batch-management';
import BatchCalculator from './pages/batch-calculator';
import Settings from './pages/settings';
import InventoryManagement from './pages/inventory-management';
import SalesAnalysis from './pages/sales-analysis';
import MarketData from './pages/market-data';
import Preferences from './pages/preferences';
import Help from './pages/help';
import AlertsDashboard from './pages/alerts-dashboard';
import LandingPage from './pages/landing-page';
import { TourProvider, useTour } from './contexts/TourContext';
import GuidedTour from './pages/main-dashboard/components/GuidedTour';
import StockSales from './pages/stock-sales';

// Patch ErrorBoundary.prototype.componentDidCatch to safely handle undefined/frozen errors
if (ErrorBoundary && ErrorBoundary?.prototype) {
  ErrorBoundary.prototype.componentDidCatch = function patchedComponentDidCatch(error, errorInfo) {
    try {
      if (error != null && typeof error === 'object') {
        error.__ErrorBoundary = true;
      }
    } catch (_) {}
    try {
      window.__COMPONENT_ERROR__?.(error, errorInfo);
    } catch (_) {}
  };
}

const EXCLUDED_TOUR_ROUTES = ['/login', '/register', '/email-confirmation'];

function GlobalTourOverlay() {
  const tour = useTour();
  const { pathname } = window.location;
  if (EXCLUDED_TOUR_ROUTES?.some(r => pathname?.startsWith(r))) return null;
  return (
    <GuidedTour
      active={tour?.active}
      currentStep={tour?.currentStep}
      stepIndex={tour?.stepIndex}
      totalSteps={tour?.totalSteps}
      onNext={tour?.nextStep}
      onPrev={tour?.prevStep}
      onSkip={tour?.skipTour}
      onComplete={tour?.completeTour}
      isLastStep={tour?.isLastStep}
      isFirstStep={tour?.isFirstStep}
      navigatingTo={tour?.navigatingTo}
    />
  );
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <OfflineBanner />
      <HelpSidePanel />
      <GlobalTourOverlay />
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/email-confirmation" element={<EmailConfirmation />} />

        {/* Protected routes */}
        <Route
          path="/main-dashboard"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Dashboard">
                <MainDashboard />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/active-batch-management"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Gestión de Lotes Activos">
                <ActiveBatchManagement />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/batch-calculator"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Calculadora de Lotes">
                <BatchCalculator />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Configuración">
                <Settings />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Inventario">
                <InventoryManagement />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory-management"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Inventario">
                <InventoryManagement />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales-analysis"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Análisis de Ventas">
                <SalesAnalysis />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market-data"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Datos de Mercado">
                <MarketData />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Preferencias">
                <Preferences />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Ayuda">
                <Help />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts-dashboard"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Panel de Alertas">
                <AlertsDashboard />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-sales"
          element={
            <ProtectedRoute>
              <ErrorBoundary screenName="Venta de Stock">
                <StockSales />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </>
  );
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary fullPage>
        <TourProvider>
          <AppRoutes />
        </TourProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
